import React, { PureComponent, ChangeEventHandler } from 'react';
import classnames from 'classnames';
import { createNumberMask } from 'text-mask-addons';
import {
  FormInput,
  TextMaskedInput,
  TextAreaInput,
  SsnMaskedInput,
  IncomeMaskedInput,
  InputWrapper,
  Label,
  PasswordToggler,
  CheckmarkIcon,
  ErrorIcon,
  InputContainer,
  DescriptionContainer,
  ErrorMessageContainer
} from './Input.styles';

const defaultProps = {
  type: 'text',
  variant: 'text',
  selectedTheme: 'light',
  label: '',
  name: '',
  description: '',
  value: '',
  error: '',
  touched: false,
  className: ''
};

export type InputProps = typeof defaultProps & {
  id: string;
  type?: string;
  variant?:
    | 'text'
    | 'textarea'
    | 'password'
    | 'email'
    | 'firstName'
    | 'lastName'
    | 'zip5'
    | 'phoneNumber'
    | 'dateOfBirth'
    | 'ssn'
    | 'income';
  selectedTheme?: 'light' | 'dark' | 'special';
  label?: string;
  name?: string;
  description?: string;
  width?: number;
  value?: string;
  error?: string;
  touched?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onFocus?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  sanitize?: (arg: any) => any;
  testHook?: string;
  className?: string;
};

type InputState = {
  showPassword: boolean;
  inputValue: string;
  hasValue: boolean;
  isFocused: boolean;
};

const noop = f => f;

class Input extends PureComponent<InputProps, InputState> {
  static defaultProps = defaultProps;

  constructor(props) {
    super(props);

    const { value } = props;
    const hasValueProp = this.hasValidValue(value);

    this.state = {
      showPassword: false,
      inputValue: value,
      hasValue: hasValueProp,
      isFocused: false
    };

    this.onInputFocus = this.onInputFocus.bind(this);
    this.onInputBlur = this.onInputBlur.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.handleAutofill = this.handleAutofill.bind(this);
    this.togglePasswordHiddenState = this.togglePasswordHiddenState.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { value } = this.props;
    if (prevProps.value !== value) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ hasValue: this.hasValidValue(value) });
    }
  }

  onInputChange(e) {
    const { sanitize } = this.props;
    const sanitizeFn = sanitize || noop;
    const newValue = sanitizeFn(e.target.value);
    this.setState({
      inputValue: newValue,
      hasValue: this.hasValidValue(newValue)
    });
  }

  onInputBlur(e) {
    const { onBlur } = this.props;
    this.setState({ isFocused: false });
    if (onBlur) onBlur(e);
  }

  onInputFocus(e) {
    const { onFocus } = this.props;
    this.setState({ isFocused: true });
    if (onFocus) onFocus(e);
  }

  // eslint-disable-next-line class-methods-use-this
  hasValidValue(v: string) {
    return v.toString().length > 0;
  }

  togglePasswordHiddenState() {
    const { showPassword } = this.state;
    this.setState({ showPassword: !showPassword });
  }

  handleAutofill(e) {
    if (e.animationName === 'onAutoFillStart') {
      this.setState({ hasValue: true });
    }
  }

  renderInput(classNames, inputProps) {
    const { variant, label } = this.props;
    switch (variant) {
      case 'textarea':
        return (
          <>
            <TextAreaInput
              className={classnames('textarea', classNames)}
              {...inputProps}
            />
            {this.renderLabel('TextArea')}
          </>
        );

      case 'firstName':
        return (
          <>
            <FormInput className={classNames} {...inputProps} />
            {this.renderLabel('First name')}
          </>
        );

      case 'lastName':
        return (
          <>
            <FormInput className={classNames} {...inputProps} />
            {this.renderLabel('Last name')}
          </>
        );

      case 'email':
        return (
          <>
            <FormInput className={classNames} {...inputProps} />
            {this.renderLabel('Email')}
          </>
        );

      case 'password':
        return (
          <>
            <FormInput className={classNames} {...inputProps} />
            {this.renderLabel('Password')}
          </>
        );

      case 'phoneNumber':
        return (
          <>
            <TextMaskedInput
              className={classNames}
              {...inputProps}
              mask="(999) 999-9999"
              maskChar=" "
            />
            {this.renderLabel('Phone number')}
          </>
        );

      case 'zip5':
        return (
          <>
            <TextMaskedInput
              className={classNames}
              {...inputProps}
              maskChar=""
              mask="99999"
            />
            {this.renderLabel('Zip Code')}
          </>
        );

      case 'dateOfBirth':
        return (
          <>
            <TextMaskedInput
              className={classNames}
              {...inputProps}
              maskChar=""
              mask="99/99/9999"
            />
            {this.renderLabel('Date of Birth')}
          </>
        );
      case 'ssn':
        // eslint-disable-next-line no-case-declarations
        const ssnMask = [
          /(\d|X)/,
          /(\d|X)/,
          /(\d|X)/,
          '-',
          /(\d|X)/,
          /(\d|X)/,
          '-',
          /(\d|X)/,
          /(\d|X)/,
          /(\d|X)/,
          /(\d|X)/
        ];
        return (
          <>
            <SsnMaskedInput
              className={classNames}
              mask={ssnMask}
              guide={false}
              {...inputProps}
            />
            {this.renderLabel('Social Security Number')}
          </>
        );

      case 'income':
        // eslint-disable-next-line no-case-declarations
        const incomeMask = createNumberMask({ prefix: '$' });
        return (
          <>
            <IncomeMaskedInput
              className={classNames}
              guide={false}
              mask={incomeMask}
              {...inputProps}
            />
            {this.renderLabel('Income')}
          </>
        );

      default:
        return (
          <>
            <FormInput className={classNames} {...inputProps} />
            {this.renderLabel(label)}
          </>
        );
    }
  }

  renderLabel(presetLabel) {
    const { id, label, variant } = this.props;
    const textAreaClass = variant === 'textarea' ? 'textarea' : '';
    return (
      <>
        <Label htmlFor={id} className={textAreaClass}>
          {!label ? presetLabel : label}
        </Label>
      </>
    );
  }

  render() {
    const {
      id,
      variant,
      selectedTheme,
      label,
      name,
      description,
      onChange,
      onBlur,
      value,
      error,
      touched,
      testHook,
      className,
      type,
      ...restProps
    } = this.props;
    const { showPassword, inputValue, hasValue, isFocused } = this.state;
    const hasValueProp = this.hasValidValue(value);
    const isPasswordVariant = variant === 'password';
    const isValid = !error && touched && hasValueProp;
    const shouldShowError = error && touched;
    const isFieldPopulated = hasValue || hasValueProp;
    const variantHasLabel =
      variant === 'password' ||
      variant === 'email' ||
      variant === 'textarea' ||
      variant === 'firstName' ||
      variant === 'lastName' ||
      variant === 'zip5' ||
      variant === 'phoneNumber' ||
      variant === 'dateOfBirth' ||
      variant === 'ssn' ||
      variant === 'income';
    const shouldShowLabel = label.length || variantHasLabel;
    const hasDescription = description.length > 0;

    const classNames = classnames(
      {
        'show-error-icon': shouldShowError,
        'is-populated': hasValue || hasValueProp,
        'is-focused': isFocused,
        'has-label': shouldShowLabel,
        'has-password': isPasswordVariant
      },
      [selectedTheme]
    );

    const inputProps = {
      id,
      variant,
      type: showPassword ? 'text' : variant,
      value: onChange ? value : inputValue,
      onBlur: onBlur || this.onInputBlur,
      onChange: onChange || this.onInputChange,
      onFocus: this.onInputFocus,
      name,
      label,
      onAnimationStart: this.handleAutofill,
      'aria-labelledby': label || id,
      ...restProps
    };

    return (
      <InputWrapper className={className}>
        <InputContainer data-cv-test={testHook} className={className}>
          {this.renderInput(classNames, inputProps)}
          {isValid && isFieldPopulated && <CheckmarkIcon />}
          {shouldShowError && <ErrorIcon />}
          {isPasswordVariant && (
            <PasswordToggler
              onClick={this.togglePasswordHiddenState}
              data-testid="password-toggler"
            >
              {showPassword ? 'Hide' : 'Show'}
            </PasswordToggler>
          )}
        </InputContainer>
        {shouldShowError && (
          <ErrorMessageContainer>{error}</ErrorMessageContainer>
        )}
        {hasDescription && (
          <DescriptionContainer>{description}</DescriptionContainer>
        )}
      </InputWrapper>
    );
  }
}

export default Input;
